import React from 'react';
import './ReportModal.css';
import PrepPanel from './PrepPanel';

const confidenceMapping = {
  low: "HIGH",     // Risk low → Confidence high
  medium: "MEDIUM",
  high: "LOW",     // Risk high → Confidence low
};


const ReportModal = ({ result, onClose }) => {
  if (!result) return null;

  // DEBUG (optional): see what we're getting
  console.log('RESULT in ReportModal:', result);
  console.log('RESULT.checks:', result.checks);

  // Helper function to determine shield color based on risk/status
  const getShieldColor = (isGood, risk = 'low') => {
    if (isGood === null || isGood === undefined) return 'gray';
    if (isGood === true) return 'green';
    if (risk === 'high') return 'red';
    if (risk === 'medium') return 'orange';
    return 'yellow';
  };

  // Helper function to format salary for display
  const formatSalary = (amount, type) => {
    if (!amount && amount !== 0) return 'Not specified';

    const formatted = Number(amount).toLocaleString();
    switch (type) {
      case 'hourly':
        return `$${formatted}/hour`;
      case 'monthly':
        return `$${formatted}/month`;
      case 'competitive':
        return 'Competitive salary';
      case 'annual':
      default:
        return `$${formatted}/year`;
    }
  };

  // Convert analysis data into yes/no statements
  const statements = [
    {
      question: 'Is the URL legitimate?',
      answer: result.safe,
      shield: getShieldColor(result.safe, result.riskLevel),
      details: result.safe
        ? 'URL appears to be from a legitimate source'
        : 'URL shows signs of potential fraud'
    },
    {
      question: 'Does the URL use secure HTTPS?',
      answer: result.summary?.hasHttps,
      shield: getShieldColor(result.summary?.hasHttps),
      details: result.summary?.hasHttps
        ? 'Connection is encrypted and secure'
        : 'Warning: Insecure HTTP connection'
    },
    {
      question: 'Does the company name match the domain?',
      answer: result.companyVerification?.match,
      shield: getShieldColor(result.companyVerification?.match),
      details: result.companyVerification?.reason || 'Company verification not available'
    },
    {
      question: 'Does the job description look professional?',
      answer: result.jobDescription?.analysis?.quality === 'good',
      shield: getShieldColor(
        result.jobDescription?.analysis?.quality === 'good',
        result.jobDescription?.analysis?.quality === 'poor' ? 'high' : 'medium'
      ),
      details: result.jobDescription?.scraped
        ? `${result.jobDescription.analysis.lineCount} lines, ${result.jobDescription.analysis.wordCount} words - ${result.jobDescription.analysis.quality} quality`
        : 'Could not analyze job description'
    },
    {
      question: 'Does the salary information seem realistic?',
      answer: result.salaryAnalysis?.riskLevel === 'low',
      shield: getShieldColor(
        result.salaryAnalysis?.riskLevel === 'low',
        result.salaryAnalysis?.riskLevel || 'medium'
      ),
      details: result.salaryAnalysis?.hasSalary
        ? `${formatSalary(
            result.salaryAnalysis?.salaryInfo?.estimatedAnnual,
            result.salaryAnalysis?.salaryInfo?.type
          )} - ${result.salaryAnalysis?.riskLevel} risk`
        : 'No salary information provided'
    },
    {
      question: 'Does the hiring volume seem normal?',
      answer: result.employeeAnalysis?.riskLevel === 'low',
      shield: getShieldColor(
        result.employeeAnalysis?.riskLevel === 'low',
        result.employeeAnalysis?.riskLevel || 'medium'
      ),
      details: result.employeeAnalysis
        ? `${result.employeeAnalysis.companySize} company with ${result.employeeAnalysis.recentJobPostings} recent job postings`
        : 'Company size data not available'
    },
    {
      question: 'Does the domain look trustworthy?',
      answer: result.summary?.domainLegitimacy?.isLikely,
      shield: getShieldColor(result.summary?.domainLegitimacy?.isLikely),
      details: result.summary?.domainLegitimacy?.isLikely
        ? `Domain legitimacy score: ${Math.round(
            result.summary.domainLegitimacy.score * 100
          )}%`
        : 'Domain shows suspicious characteristics'
    },
    {
      question: 'Does the URL match career page patterns?',
      answer: result.summary?.careerPagePattern,
      shield: getShieldColor(result.summary?.careerPagePattern),
      details: result.summary?.careerPagePattern
        ? 'URL follows legitimate job posting patterns'
        : "URL doesn't follow typical career page patterns"
    }
  ].filter(
    (statement) => statement.answer !== undefined && statement.answer !== null
  );

    // Overall risk assessment
    const overallRisk = result.riskLevel || 'low';
    const overallSafe = result.safe;


      // LLM + OSINT helpers for P2 row
    const prepLLM = result.prepLLM || {};
    const osint = result.osint || {};

    // Map scam reports → yes/no/unknown for the P2 question
    const scamPresent = osint?.scamReports?.present;
    let scamReportsValue;
    if (scamPresent === 'no') {
      // No scam reports => good
      scamReportsValue = 'yes';
    } else if (scamPresent === 'yes' || scamPresent === 'some') {
      // Scam reports exist => bad
      scamReportsValue = 'no';
    } else {
      // unknown / missing
      scamReportsValue = 'unknown';
    }


// === PREP rows mapping ============================================
  const checkToYN = (v) => {
    if (v === true || v === 'yes' || v === 'match') return 'yes';
    if (v === false || v === 'no' || v === 'mismatch') return 'no';
    if (v === 'warn') return 'unknown';
    return 'unknown';
  };

  const checks = result.checks || [];
  const findCheck = (id) => checks.find((c) => c.id === id) || {};

  // P row – Posting & Application
  const pRow = {
    label: 'P',
    name: 'Posting & Application',
    questions: [
      {
        id: 'p_official_host',
        text: "Posting Validated on Employer's Website/ATS",
        value: checkToYN(findCheck('p_official_host').value)
      },
      {
        id: 'p_https',
        text: 'Posting is Secured By HTTPS Security Protocol',
        value: checkToYN(findCheck('p_https').value)
      },
      {
        id: 'p_pii_clean',
        text: 'Job Posting Free of Payment Requirement or Personal Sensitive Information',
        value: checkToYN(findCheck('p_pii_clean').value)
      },
      {
        id: 'p_trackers_ok',
        text: 'Job Posting Link Puts Trackers on Applicants',
        value: checkToYN(findCheck('p_trackers_ok').value)
      },
      {
        id: 'p_title_match',
        text: 'Job Title on Posting Matches Title on Application Link',
        value: checkToYN(findCheck('p_title_match').value)
      }
    ]
  };

  // R row – Recruiter
  const rRow = {
  label: 'R',
  name: 'Recruiter',
  questions: [
    {
      id: 'r_from_employer',
      text: "Recruiter uses official company email domain",
      value: checkToYN(findCheck('r_from_employer').value)
    },
    {
      id: 'r_incorporated',
      text: 'Recruiter’s email domain is properly registered',
      value: checkToYN(findCheck('r_incorporated').value)
    },
    {
      id: 'r_relationship',
      text: 'Employer–recruiter relationship confirmed',
      value: checkToYN(findCheck('r_relationship').value)
    },
    {
      id: 'r_multi_post',
      text: 'No signs of recruiter mass-posting identical jobs',
      value: checkToYN(findCheck('r_multi_post').value)
    },
    {
      id: 'r_email_spam',
      text: 'Recruiter’s email passes spam & security checks',
      value: checkToYN(findCheck('r_email_spam').value)
    }
  ]
};


  // E row – Employer
    const eRow = {
      label: 'E',
      name: 'Employer',
      questions: [
        {
          id: 'e_match',
          text: 'Employer brand matches legal name',
          value: checkToYN(findCheck('e_match').value)
        },
        {
          id: 'e_in_registries',
          text: 'Employer found in government or public business registries',
          value: checkToYN(findCheck('e_in_registries').value)
        },
        {
          id: 'e_incorporation',
          text: 'Employer is legally incorporated and active',
          value: checkToYN(findCheck('e_incorporation').value)
        },
        {
          id: 'e_office_city',
          text: 'Employer has an office in the job’s listed city',
          value: checkToYN(findCheck('e_office_city').value)
        },
        {
          id: 'e_industry_auth',
          text: 'Employer appears in industry databases (DUNS/NAICS)',
          value: checkToYN(findCheck('e_industry_auth').value)
        }
      ]
    };


  // P2 row – OSINT & Risk
      const p2Row = {
      label: 'P',
      name: 'Hiring Process & Risk',
      questions: [
        {
          id: 'p2_transparent',
          text: 'Hiring process is transparent and documented',
          value: checkToYN(prepLLM.hiring_process_transparent)
        },
        {
          id: 'p2_state_variation',
          text: 'Hiring process compliance varies from state to state',
          value: checkToYN(prepLLM.hiring_process_state_variation)
        },
        {
          id: 'p2_scam_reports',
          text: 'No reports of hiring scams from other candidates',
          value: scamReportsValue
        },
        {
          id: 'p2_comp_benchmark',
          text: 'Compensation range within industry benchmark of peers',
          value: checkToYN(prepLLM.comp_within_benchmark)
        },
        {
          id: 'p2_timeline_clear',
          text: 'Closing date and hiring process completion confirmed by employer',
          value: checkToYN(prepLLM.hiring_timeline_clear)
        }
      ]
    };



  const prepRows = {
    p: pRow,
    r: rRow,
    e: eRow,
    p2: p2Row
  };
  // =======================================================
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>Job Safety Report</h2>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Overall Assessment */}
        <div className={`overall-assessment ${overallRisk}`}>
          <div className="assessment-icon">
            <div
              className={`shield-icon shield-${getShieldColor(
                overallSafe,
                overallRisk
              )}`}
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
              </svg>
            </div>
          </div>
          <div className="assessment-text">
            <h3>
              {overallSafe ? 'Job Posting Appears Safe' : '⚠️ Potential Issues Detected'}
            </h3>
              <p className={`risk-level risk-${overallRisk}`}>
                Confidence in Posting: {confidenceMapping[overallRisk]}
              </p>
          </div>
        </div>

        {/* PREP survey – row P only */}
        <div style={{ marginTop: '16px', marginBottom: '8px' }}>
          <PrepPanel prepRows={prepRows} />
        </div>

        
        {/* {result.salaryAnalysis?.hasSalary && (
          <div className="salary-summary-section">
            <h3>💰 Salary Analysis</h3>
            <div className="salary-info">
              <div className="salary-amount">
                <strong>Estimated Compensation:</strong>{' '}
                {formatSalary(
                  result.salaryAnalysis?.salaryInfo?.estimatedAnnual,
                  result.salaryAnalysis?.salaryInfo?.type
                )}
              </div>
              {result.salaryAnalysis?.salaryInfo?.isRange && (
                <div className="salary-range">
                  Range: $
                  {result.salaryAnalysis.salaryInfo.minSalary?.toLocaleString()} - $
                  {result.salaryAnalysis.salaryInfo.maxSalary?.toLocaleString()}
                </div>
              )}
              <div
                className={`salary-risk risk-${result.salaryAnalysis?.riskLevel}`}
              >
                Risk Level: {result.salaryAnalysis?.riskLevel?.toUpperCase()}
              </div>
            </div>
          </div>
        )}
        
        {result.salaryAnalysis?.redFlags &&
          result.salaryAnalysis.redFlags.length > 0 && (
            <div className="salary-flags-section">
              <h3>💰 Salary Red Flags</h3>
              <ul>
                {result.salaryAnalysis.redFlags.map((flag, index) => (
                  <li key={index} className="red-flag">
                    {flag}
                  </li>
                ))}
              </ul>
              {result.salaryAnalysis.suspiciousReasons &&
                result.salaryAnalysis.suspiciousReasons.length > 0 && (
                  <div className="suspicious-reasons">
                    <strong>Why this is suspicious:</strong>
                    <ul>
                      {result.salaryAnalysis.suspiciousReasons.map(
                        (reason, index) => (
                          <li key={index}>{reason}</li>
                        )
                      )}
                    </ul>
                  </div>
                )}
            </div>
          )}

        
        {result.employeeAnalysis?.redFlags &&
          result.employeeAnalysis.redFlags.length > 0 && (
            <div className="red-flags-section">
              <h3>🚩 Company Red Flags</h3>
              <ul>
                {result.employeeAnalysis.redFlags.map((flag, index) => (
                  <li key={index}>{flag}</li>
                ))}
              </ul>
            </div>
          )}

        
        {(result.salaryAnalysis?.greenFlags?.length > 0 ||
          result.employeeAnalysis?.greenFlags?.length > 0) && (
          <div className="green-flags-section">
            <h3>✅ Positive Indicators</h3>
            <ul>
              {result.salaryAnalysis?.greenFlags?.map((flag, index) => (
                <li key={`salary-${index}`} className="green-flag">
                  💰 {flag}
                </li>
              ))}
              {result.employeeAnalysis?.greenFlags?.map((flag, index) => (
                <li key={`employee-${index}`} className="green-flag">
                  🏢 {flag}
                </li>
              ))}
            </ul>
          </div>
        )} */}


        {/* <div className="modal-actions">
          <button
            className="action-button secondary"
            onClick={() => {
              console.log('📄 FULL REPORT DATA:');
              console.log(result);
              alert('Full report data logged to console (F12)');
            }}
          >
            View Technical Details
          </button>
          <button
            className="action-button primary"
            onClick={() => {
              navigator.clipboard.writeText(result.finalURL);
              alert('URL copied to clipboard!');
            }}
          >
            Copy URL
          </button>
          <button className="action-button primary" onClick={onClose}>
            Close Report
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default ReportModal;
